<ng-container *transloco="let t">
  @let showToolbarButtons = currentUserRole | showToolbarButtons;
  <ejs-appbar colorMode="Primary">
    <!--
      Single wrapper div is the idiomatic Syncfusion AppBar pattern. The
      material theme stretches `.e-appbar > div:first-child` to `width: 100%`,
      so we wrap our three sections (left / spacer / right) inside one div
      and arrange them ourselves via flex.
    -->
    <div class="appbar-content">
      <div class="appbar-left">
        <a class="appbar-logo-link" [routerLink]="homeRoute">
          <img
            ngSrc="../../assets/images/goatness-software-logo.png"
            alt="GOAT logo"
            priority
            width="127"
            height="45"
          />
        </a>
        @if (currentUserRole === RoleType.OrganizationAdmin && hasMultipleOrganizations()) {
          <button ejs-button class="org-switch-button" cssClass="e-inherit" (click)="openOrganizationSwitchDialog()">
            <span class="e-icons e-organization"></span>
            <span class="org-name">{{ activeOrganizationName() || t('Select Organization') }}</span>
          </button>
        }
      </div>

      <div class="e-appbar-spacer"></div>

      <div class="appbar-right">
        @if (showToolbarButtons) {
          <div class="appbar-actions">
            @if (contactsEnabled()) {
              <ejs-tooltip opensOn="Hover" [content]="t('Calendar')" position="BottomCenter">
                <a
                  id="calendar"
                  class="appbar-action"
                  [routerLink]="'/calendar'"
                  routerLinkActive="is-active"
                  [attr.aria-label]="t('Calendar')"
                >
                  <span class="e-icons e-day"></span>
                  <!--
                  The badge counts follow-ups now overdue, and it is a badge on YOUR
                  workload: it keeps counting yours even while the calendar page is
                  pointed at somebody else.
                -->
                  @if (overdueCount(); as count) {
                    <span class="appbar-action__badge">{{ count | shortenCounter }}</span>
                  }
                </a>
              </ejs-tooltip>
            }

            <ejs-tooltip opensOn="Hover" [content]="t('Chat')" position="BottomCenter">
              <a
                id="chat"
                class="appbar-action"
                [routerLink]="'/chat'"
                routerLinkActive="is-active"
                [attr.aria-label]="t('Chat')"
              >
                <!-- Inline SVG two-bubble chat icon: the e-icons font only has a
                     filled single-bubble glyph, which renders bolder than the
                     neighbouring outlined icons. -->
                <span class="chat-bubbles-icon">
                  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                    <g
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linejoin="round"
                      stroke-linecap="round"
                    >
                      <path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" />
                      <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1" />
                    </g>
                  </svg>
                </span>
                @if (unreadMessagesCount(); as count) {
                  <span class="appbar-action__badge">{{ count | shortenCounter }}</span>
                }
              </a>
            </ejs-tooltip>

            <ejs-tooltip opensOn="Hover" [content]="t('Notification')" position="BottomCenter">
              <button
                id="notification"
                ejs-dropdownbutton
                target="#listview"
                cssClass="e-inherit e-caret-hide appbar-action-popup"
                class="appbar-action"
                [attr.aria-label]="t('Notification')"
              >
                <span class="e-icons e-notes"></span>
                @if (unreadNotificationsCount(); as count) {
                  <span class="appbar-action__badge">{{ count | shortenCounter }}</span>
                }
              </button>
            </ejs-tooltip>
          </div>
        }

        <ejs-menu [items]="menuItems()" cssClass="e-inherit" (select)="executeAction($event)">
          <ng-template #template let-data>
            @if (data.properties.text === ProfileMenuItem.Icon) {
              @let roleKey = roleLabelKey();
              @let nameLabel = currentUserFullName();
              @let roleLabel = roleKey ? t(roleKey) : '';
              @let pillLabel = nameLabel && roleLabel ? nameLabel + ' — ' + roleLabel : nameLabel || roleLabel;
              <div class="user-pill" [attr.aria-label]="t('Signed in as') + ' ' + pillLabel" [attr.title]="pillLabel">
                <span class="user-pill__avatar">
                  @if (currentUserInitials(); as initials) {
                    {{ initials }}
                  } @else {
                    <span class="e-icons e-user"></span>
                  }
                </span>
                @if (currentUserRole) {
                  <span class="user-pill__info">
                    <span class="user-pill__name">{{ currentUserFullName() }}</span>
                    @if (roleKey) {
                      <span class="user-pill__role">{{ t(roleKey) }}</span>
                    }
                  </span>
                }
              </div>
            } @else {
              <div class="language-selector-container">
                <span
                  [ngClass]="{
                    selected: data.properties.id === ProfileMenuItem.Language,
                  }"
                >
                  {{ t(data.properties.text) }}
                </span>
              </div>
            }
          </ng-template>
        </ejs-menu>
      </div>
    </div>
  </ejs-appbar>

  <!--
    Notification dropdown content lives outside the appbar so it doesn't
    occupy a slot in any of the appbar flex layouts. Syncfusion's
    `target="#listview"` resolves by global ID, so position in DOM doesn't
    matter — only that the element exists somewhere. Gated on
    `showToolbarButtons` so roles without notification access don't pay
    the NotificationsListService initial-fetch cost.
  -->
  @if (showToolbarButtons) {
    <goatsports-notifications-list id="listview" />
  }

  <!-- Host for dynamic dialogs -->
  <ng-template adHost></ng-template>

  @if (organizationMismatch(); as mismatch) {
    <goatsports-organization-changed-dialog [mismatch]="mismatch" />
  }

  <ejs-sidebar
    #sidebar
    width="220"
    dockSize="64"
    target=".main-sidebar-content"
    [enableDock]="true"
    [type]="'Push'"
    [enableGestures]="false"
    (click)="sidebar.toggle()"
    [animate]="true"
    [enablePersistence]="true"
  >
    <div class="row">
      <section style="display: flex; flex-direction: column; justify-content: space-between">
        <div>
          @for (data of currentUserRole! | showRoleBasedNavigation: routerUrl()!; track data.id!; let i = $index) {
            @if (data.text !== 'Settings') {
              @if (sidebar.isOpen) {
                <a
                  class="sidebar-tooltip"
                  style="display: flex; align-items: center"
                  [id]="'target' + i"
                  (click)="$event.stopPropagation()"
                  [routerLink]="data.id"
                  [routerLinkActive]="['e-active']"
                  [replaceUrl]="false"
                >
                  <goatsports-icon-status [dataNav]="{ name: data.text, isSelected: data.selected }" />
                  <p>{{ t(data.text!) }}</p>
                </a>
              } @else {
                <div class="collapsed-container">
                  <a
                    class="sidebar-tooltip"
                    (click)="$event.stopPropagation()"
                    [routerLink]="data.id"
                    [routerLinkActive]="['e-active']"
                    [replaceUrl]="false"
                  >
                    <ejs-tooltip
                      opensOn="Hover"
                      [content]="t(data.text!)"
                      [target]="'#target' + i"
                      position="BottomRight"
                    >
                      <goatsports-icon-status
                        [id]="'target' + i"
                        [dataNav]="{ name: data.text, isSelected: data.selected }"
                      />
                    </ejs-tooltip>
                  </a>
                </div>
              }
            }
          }
        </div>

        <div>
          @for (data of currentUserRole! | showRoleBasedNavigation: routerUrl()!; track data.id!; let i = $index) {
            @if (data.text === 'Settings') {
              @if (sidebar.isOpen) {
                <a
                  class="sidebar-tooltip"
                  style="display: flex; align-items: center"
                  [id]="'target' + i"
                  (click)="$event.stopPropagation()"
                  [routerLink]="data.id"
                  [routerLinkActive]="['e-active']"
                  [replaceUrl]="false"
                >
                  <goatsports-icon-status [dataNav]="{ name: data.text, isSelected: data.selected }" />
                  <p>{{ t(data.text!) }}</p>
                </a>
              } @else {
                <div class="collapsed-container">
                  <a
                    class="sidebar-tooltip"
                    (click)="$event.stopPropagation()"
                    [routerLink]="data.id"
                    [routerLinkActive]="['e-active']"
                    [replaceUrl]="false"
                  >
                    <ejs-tooltip
                      opensOn="Hover"
                      [content]="t(data.text!)"
                      [target]="'#target' + i"
                      position="BottomRight"
                    >
                      <goatsports-icon-status
                        [id]="'target' + i"
                        [dataNav]="{ name: data.text, isSelected: data.selected }"
                      />
                    </ejs-tooltip>
                  </a>
                </div>
              }
            }
          }
        </div>
      </section>

      <div class="collapse-row">
        <span [ngClass]="sidebar.isOpen ? 'collapse-icon' : 'expand-icon'"></span>
        @if (sidebar.isOpen) {
          <span>{{ t('Collapse') }}</span>
        }
      </div>
    </div>
  </ejs-sidebar>

  <div class="main-sidebar-content">
    <div style="height: 100%">
      @if (!isErrorThrown()) {
        <router-outlet />
      } @else {
        <goatsports-error-messages />
      }
    </div>
  </div>
</ng-container>
